import React, { useContext } from "react";
import * as ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import "./index.css";
import "./styles/theme.css";
import Root from "./routes/root";
import Sales from "./routes/accounting/sales";
import AccountingClose from "./routes/accounting/accountingClose";
import Users from "./routes/users";
import LoadPlan from "./routes/planes/loadPlan/loadPlan";
import { ConfigProvider } from "antd";
import customTheme from "./theme";
import { openNotification } from "./components/notifications";
import AccountingDetails from "./routes/accounting/accountingDetails";
import User from "./routes/user";
import CashBox from "./routes/accounting/cashBox";
import RegisterForm from "./routes/auth/RegisterForm";
import LoginForm from "./routes/auth/LoginForm";
import PrivateRoute from "./components/PrivateRoute";
import Home from "./routes/home";
import Plans from "./routes/planes/salePlan/plans";
import PublicLayout from "./components/PublicLayout";
import AuthProvider, { AuthContext } from "./components/AuthProvider";
import { LoadingProvider } from "./components/LoadingContext";
import Tamagotchi from "./routes/tamagotchi/Tamagotchi";
import Event from "./routes/sessions/event";
import EventForm from "./components/EventForm";
import Achievements from "./routes/achievements/Achievements";
import Courtesies from "./routes/courtesies/Courtesies";
import LifeTimeValue from "./routes/statistics/LifeTimeValue";
import Retention from "./routes/statistics/Retention";
import Agreements from "./routes/agreements/Agreements";
import Agreement from "./routes/agreements/Agreement";
import UsedAgreement from "./routes/agreements/UsedAgreement";
import PCCDashboard from "./routes/pcc/PCCDashboard";
import MyPCC from "./routes/pcc/MyPCC";
import ResetPasswordPage from "./routes/auth/ResetPasswordPage";
import MembershipDistribution from "./routes/statistics/MembershipDistribution";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CollectPayment from "./routes/accounting/CollectPayment";
import Wrapped from "./routes/Wrapped";
import CourtesiesStatistics from "./routes/statistics/CourtesiesStatistics";
import CRM from "./routes/crm/CRM";
import KnowledgeBase from "./routes/crm/KnowledgeBase";
import Chatbot from "./components/Chatbot/Chatbot";
import Bienvenida from "./routes/Bienvenida";

// Set theme stylesheet from environment at runtime (overrides default link in public/index.html)
(function setThemeFromEnv() {
  try {
    const themeName = process.env.REACT_APP_THEME || process.env.NODE_ENV || "production";
    const href = `${process.env.PUBLIC_URL}/styles/theme.${themeName}.css`;
    let link = document.getElementById("theme-link");
    if (!link) {
      link = document.createElement("link");
      link.id = "theme-link";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
  } catch (e) {
    // Ignore in non-browser environments
  }
})();

// Set document title from environment variable if provided
(function setTitleFromEnv() {
  try {
    const title = process.env.REACT_APP_TITLE;
    if (title && typeof document !== 'undefined') {
      document.title = title;
    }
  } catch (e) {
    // ignore when not in browser or env unavailable
  }
})();

const App = () => {
  const { isAuthenticated } = useContext(AuthContext);

  React.useEffect(() => {
    const savedNotification = localStorage.getItem("notification");
    if (savedNotification) {
      const { type, title, description } = JSON.parse(savedNotification);
      openNotification(type, title, description);
      localStorage.removeItem("notification");
    }
  }, []);

  return (
    <ConfigProvider theme={customTheme}>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout />}>
            <Route path="login" element={<LoginForm />} />
            <Route path="register" element={<RegisterForm />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="agreements/:slug/users/:user_id" element={<UsedAgreement />} />
            <Route path="" element={<Root />} />
          </Route>

          {/* Conditionally Render Plans Route */}
          {isAuthenticated ? (
            <Route path="/" element={<PrivateRoute />}>
              <Route path="/plans" element={<Plans />} />
            </Route>
          ) : (
            <Route path="/" element={<PublicLayout />}>
              <Route path="/plans" element={<Plans />} />
            </Route>
          )}


          {/* Protected Routes */}
          {/* TODO: protect with features*/}
          <Route path="/" element={<PrivateRoute />}>
            <Route path="accounting" element={<CashBox />} />
            <Route path="accounting/close" element={<AccountingClose />} />
            <Route path="accounting/details" element={<AccountingDetails />} />
            <Route path="accounting/collect_payment" element={<CollectPayment />} />
            <Route path="achievements/:user_id" element={<Achievements />} />
            <Route path="ranking/:user_id" element={<Achievements />} />
            <Route path="sales" element={<Sales />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:user_id" element={<User />} />
            <Route path="home" element={<Home />} />
            <Route path="planes/load" element={<LoadPlan />} />
            <Route path="events/:event_id" element={<Event />} />
            <Route path="events/new" element={<EventForm />} />
            <Route path="tamagotchi/:user_id" element={<Tamagotchi />} />
            <Route path="courtesies" element={<Courtesies />} />
            <Route path="statistics/life_time_value" element={<LifeTimeValue />} />
            <Route path="statistics/retention" element={<Retention />} />
            <Route path="statistics/membership-distribution" element={<MembershipDistribution />} />
            <Route path="statistics/courtesies" element={<CourtesiesStatistics />} />
            <Route path="agreements" element={<Agreements />} />
            <Route path="agreements/:slug" element={<Agreement />} />
            <Route path="pqrs" element={<PCCDashboard />} />
            <Route path="my_pccs" element={<MyPCC />} />
            <Route path="/users/:userId/wrapped/:year" element={<Wrapped />} />
            <Route path="crm" element={<CRM />} />
            <Route path="crm/knowledge" element={<KnowledgeBase />} />
            <Route path="chatbot" element={<Chatbot />} />
            <Route path="bienvenida" element={<Bienvenida />} />
          </Route>
        </Routes>
      </Router>
    </ConfigProvider>
  );
};

const queryClient = new QueryClient();

const initializeApp = () => {
  ReactDOM.createRoot(document.getElementById("root")).render(
    <AuthProvider>
      <LoadingProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </LoadingProvider>
    </AuthProvider>
  );
};

initializeApp();